import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { decodeEquipmentId, singleLineAddress } from '../../helpers/util'
import useFleet from '../../hooks/useFleet'
import useRequest from '../../hooks/useRequest'
import useAuth from '../../hooks/useAuth'
import ContentLayout from '../../components/ContentLayout/ContentLayout'
import MainHeading from '../../components/MainHeading/MainHeading'
import Button from '../../components/Button/Button'
import { DocumentAddIcon } from '@heroicons/react/outline'
import TabPanel from '../../components/TabPanel/TabPanel'
import Overview from './components/tabs/Overview'
import { useCallback } from 'react'
import useGlobalLoading from '../../hooks/useGlobalLoading'
import useIntercom from '../../hooks/useIntercom'
import ServiceHistory from './components/tabs/ServiceHistory'
import PlannedMaintenance from './components/tabs/PlanedMaintenance'
import ServiceEstimate from './components/tabs/ServiceEstimate'
import AppRoutes from '../../routes/appRoutes'
import { useHistory } from 'react-router-dom'
import { parse } from 'qs'
import { getFleetById } from '../../../../indexedDb/fleets'
import { useNetworkStatus } from '../../utils/networkStatus'
import { enforceCheckDuplicatesStep } from '../CreateRequestStepper.js/components/Repair/utils/updateStepState'

const FleetAssetPage = props => {
    const { match } = props
    const { isOffline } = useNetworkStatus()
    const [asset, setAsset] = useState({})
    const [allRequests, setAllRequest] = useState([])
    const [openPMS, setOpenPMS] = useState([])
    const [estimateRequests, setEstimateRequests] = useState([])
    const { handleGetAsset, handleGetAssetById } = useFleet()
    const {
        handleListRepairRequestForEquipment,
        handleListPMRequestForEquipment,
        handleListEstimateRequestForEquipment,
    } = useRequest()
    const { user } = useAuth()
    const { changeLoading } = useGlobalLoading()
    const [assetImage, setAssetImage] = useState(null)

    useEffect(() => {
        if (isOffline) {
            setAssetImage('/images/Fallback.jpg')
        }
    }, [isOffline])

    const history = useHistory()

    const tab = parse(window.location.search, {
        ignoreQueryPrefix: true,
    })?.activeTab

    const firstTab = {
        key: tab ? tab : 'overviewTab',
        active: true,
    }

    const { loadIntercom } = useIntercom()
    const assetId = parse(window.location.search, { ignoreQueryPrefix: true })
        ?.assetId

    const onListRepairRequestSuccess = response => {
        changeLoading(false)
        setAllRequest([
            ...response.data.openRequests,
            ...response.data.closedRequests,
        ])
    }
    const onListPMRequestSuccess = response => {
        changeLoading(false)

        setOpenPMS([...response.data.openPMs, ...response.data.completedPMs])
    }
    const onListEstimateRequestSuccess = response => {
        changeLoading(false)

        setEstimateRequests([...response?.data?.openEstimates])
    }

    const onListRequestError = response => {
        console.log(response)
        changeLoading(false)
    }

    let equipmentId = decodeEquipmentId(match?.params?.equipmentId)

    const onGetAssetSuccess = response => {
        const assetResponse = response.data?.asset

        setAsset(assetResponse)
        setAssetImage(assetResponse?.image)
        changeLoading(false)
    }

    const onLoadServiceHistory = () => {
        changeLoading(true)
        handleListRepairRequestForEquipment(
            asset.salesforceID,
            asset.soldToSalesforceID,
            onListRepairRequestSuccess,
            onListRequestError,
        )
    }

    const onLoadsPMS = () => {
        changeLoading(true)
        handleListPMRequestForEquipment(
            asset.salesforceID,
            asset.soldToSalesforceID,
            onListPMRequestSuccess,
            onListRequestError,
        )
    }

    const onLoadEstimates = () => {
        changeLoading(true)
        handleListEstimateRequestForEquipment(
            asset.salesforceID,
            asset.soldToSalesforceID,
            onListEstimateRequestSuccess,
            onListRequestError,
        )
    }

    const onGetAssetError = error => {
        const { message } = error.response?.data ?? {}
        console.log(message)
        changeLoading(false)
    }

    const getAssetInfo = async () => {
        changeLoading(true)
        try {
            if (isOffline) {
                const assetResponse = await getFleetById(equipmentId)
                setAsset(assetResponse)
                changeLoading(false)
            } else {
                return handleGetAsset(
                    {
                        assetId: equipmentId,
                        companyId: user?.company?.id,
                        getRequests: false,
                    },
                    onGetAssetSuccess,
                    onGetAssetError,
                )
            }
        } catch (error) {
            onGetAssetError(error)
        }
        // } finally {
        //     changeLoading(false)
        // }
    }

    const onGetAssetByIdSuccess = response => {
        const { asset } = response.data

        const shipto = user?.company?.shiptos?.find(
            item => item?.id === asset?.shipto_id,
        )
        if (shipto) {
            asset.location = shipto?.name
            asset.address = singleLineAddress(
                shipto.shipping_street1,
                shipto.shipping_city,
                shipto.shipping_state,
                shipto.shipping_zip,
            )
        }
        setAsset(asset)
        changeLoading(false)
    }

    const getAssetInfoById = () => {
        changeLoading(true)
        return handleGetAssetById(
            {
                equipmentId: assetId,
                companyId: user?.company?.id,
            },
            onGetAssetByIdSuccess,
            onGetAssetError,
        )
    }

    useEffect(() => {
        if (!asset?.salesforceID) {
            if (assetId) {
                getAssetInfoById()
            } else {
                getAssetInfo()
            }
        }

        if (!isOffline) {
            loadIntercom()
        } else {
            if (window.Intercom) {
                window.Intercom('shutdown')
            }
        }
    }, [asset, isOffline])

    const renderTabs = useCallback(() => {
        return [
            {
                title: 'Overview',
                content: (
                    <Overview
                        onGetAssetInfo={
                            assetId ? getAssetInfoById : getAssetInfo
                        }
                        assetInfo={asset}
                        cords={asset?.coordinates}
                        assetImage={assetImage}
                        setAssetImage={setAssetImage}
                    />
                ),
                key: 'overviewTab',
            },
            {
                title: 'Service History',
                content: (
                    <ServiceHistory
                        assetInfo={asset}
                        requests={allRequests}
                        loadServices={onLoadServiceHistory}
                    />
                ),
                key: 'serviceHistoryTab',
                hasPadding: false,
                disabled: isOffline,
            },
            {
                title: 'Planned Maintenance',
                content: (
                    <PlannedMaintenance
                        assetInfo={asset}
                        loadPlannedMaintenance={onLoadsPMS}
                        requests={openPMS}
                    />
                ),
                key: 'plannedMaintenanceTab',
                hasPadding: false,
                disabled: isOffline,
            },
            {
                title: 'Service Estimates',
                content: (
                    <ServiceEstimate
                        assetInfo={asset}
                        loadServiceEstimates={onLoadEstimates}
                        requests={estimateRequests}
                    />
                ),
                key: 'serviceEstimateTab',
                hasPadding: false,
                disabled: isOffline,
            },
        ]
    }, [asset, allRequests, openPMS, estimateRequests, assetImage, isOffline])

    const selectedAssetName = asset?.customerAlias
        ? asset?.customerAlias
        : asset?.name

    const createRequestNextStep = (history, selectedAssetName) => {
        // Enforce that the next step becomes "Check Duplicates"
        enforceCheckDuplicatesStep(selectedAssetName)

        history?.push({
            pathname: AppRoutes.createRequestStepper(),
            search: `?equipmentId=${asset?.salesforceID}`,
            state: { asset },
        })
    }

    return (
        <ContentLayout>
            <MainHeading
                title={
                    <div className="flex flex-col gap-y-2 w-full h-auto">
                        <a
                            role="button"
                            onClick={() => {
                                history?.goBack()
                            }}
                            className="text-textDarkGray text-sm font-medium"
                        >
                            {'< Go Back'}
                        </a>
                        <p className="md:text-2xl w-full min-w-max text-lg font-medium text-black flex md:flex-row flex-col md:items-center items-start justify-start leading-5">
                            <span className="text-black30">
                                Asset Details:{' '}
                            </span>
                            <span className="lg:ml-2">{selectedAssetName}</span>
                        </p>
                    </div>
                }
                actions={[
                    <Button
                        className="flex items-center justify-center md:w-auto w-full"
                        key="createRequest"
                        onClick={() => {
                            createRequestNextStep(history, selectedAssetName)
                        }}
                        text="Create Request"
                        variant="primary"
                        icon={
                            <DocumentAddIcon className="text-yellow stroke-current stroke-2 w-6 h-6 mr-2" />
                        }
                    />,
                ]}
            />
            <TabPanel
                firstTab={firstTab}
                background="bg-white"
                tabs={renderTabs()}
            />
        </ContentLayout>
    )
}

FleetAssetPage.propTypes = {
    backLink: PropTypes.string,
    match: PropTypes.shape({
        params: PropTypes.shape({
            equipmentId: PropTypes.string,
        }),
    }),
}

export default FleetAssetPage
